import { type ImgHTMLAttributes, useCallback, useMemo, useState } from 'react';
import { cn } from '../../lib/utils';

const sizeStyles = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-12 text-base',
} as const;

export type AvatarSize = keyof typeof sizeStyles;

export type AvatarProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'size' | 'name'
> & {
  /** Display name for initials when `src` is missing or the image fails to load. */
  name?: string;
  size?: AvatarSize;
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0]) {
    const w = parts[0];
    return w.length >= 2 ? w.slice(0, 2).toUpperCase() : w.toUpperCase();
  }
  return '?';
}

export function Avatar({
  src,
  alt = '',
  name,
  size = 'md',
  className,
  onError,
  ...imgRest
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;
  const initials = useMemo(() => (name ? initialsFromName(name) : '?'), [name]);

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setFailed(true);
      onError?.(e);
    },
    [onError],
  );

  if (showImage) {
    return (
      <img
        src={src}
        alt={alt}
        onError={handleError}
        className={cn(
          'rounded-full border border-border object-cover',
          sizeStyles[size],
          className,
        )}
        {...imgRest}
      />
    );
  }

  return (
    <span
      role='img'
      aria-label={alt || name || 'User'}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full border border-border bg-accent font-semibold text-foreground',
        sizeStyles[size],
        className,
      )}
    >
      {initials}
    </span>
  );
}
